import { useState, useEffect } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ListPagination } from '@/components/ListPagination';
import { useServerPagination } from '@/hooks/useServerPagination';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { useTableSort } from '@/hooks/useTableSort';
import { Search } from 'lucide-react';
import { formatBusinessDate } from '@/lib/format';
import { driverProfileName } from '@/lib/driverProfile';

export default function LoansRecords() {
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 300);

    const pagination = useServerPagination({ totalCount: 0 });

    const { sortKey, sortDirection, handleSort } = useTableSort({
        defaultSortKey: 'created_at',
        defaultSortDirection: 'desc',
        onSortChange: pagination.resetPage,
    });

    const { data: allLoansForSearch } = useQuery({
        queryKey: ['loans-search-data'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('loans')
                // `admin_id` IS SELECTED SO THE DRIVER'S OWN ROW CAN BE PICKED OUT — see
                // `driverProfileOf`; the embed can carry an admin's test profile beside it.
                .select(`id, description, contracts (drivers (profiles (id, admin_id, first_name, last_name)))`);
            if (error) throw error;
            return data;
        },
    });

    const getFilteredLoanIds = () => {
        if (!allLoansForSearch || !debouncedSearch) return null;
        const searchLower = debouncedSearch.toLowerCase();
        return allLoansForSearch
            .filter(loan => {
                // The name searched is the name shown: an admin's test profile must not make the
                // driver's advance findable under the admin's name either.
                const driverName = (driverProfileName(loan.contracts?.drivers?.profiles) ?? '').toLowerCase();
                return loan.description?.toLowerCase().includes(searchLower) || driverName.includes(searchLower);
            })
            .map(loan => loan.id);
    };

    const filteredLoanIds = getFilteredLoanIds();

    const { data: countData } = useQuery({
        queryKey: ['loans-count', debouncedSearch, filteredLoanIds],
        queryFn: async () => {
            if (filteredLoanIds !== null && filteredLoanIds.length === 0) return 0;
            let query = supabase.from('loans').select('*', { count: 'exact', head: true });
            if (filteredLoanIds !== null) query = query.in('id', filteredLoanIds);
            const { count, error } = await query;
            if (error) throw error;
            return count || 0;
        },
        enabled: allLoansForSearch !== undefined,
    });

    const paginationWithCount = useServerPagination({ totalCount: countData || 0 });

    useEffect(() => { paginationWithCount.resetPage(); }, [debouncedSearch]);

    const { data: loans, isLoading } = useQuery({
        queryKey: ['loans', paginationWithCount.currentPage, sortKey, sortDirection, debouncedSearch, filteredLoanIds],
        queryFn: async () => {
            if (filteredLoanIds !== null && filteredLoanIds.length === 0) return [];
            let query = supabase
                .from('loans')
                .select(`*, transactions (amount, transaction_at), contracts (drivers (profiles (id, admin_id, first_name, last_name)))`)
                .order(sortKey ?? 'created_at', { ascending: sortDirection === 'asc' });
            if (filteredLoanIds !== null) query = query.in('id', filteredLoanIds);
            query = query.range(paginationWithCount.from, paginationWithCount.to);
            const { data, error } = await query;
            if (error) throw error;
            return data;
        },
        enabled: countData !== undefined && allLoansForSearch !== undefined,
    });

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
    };

    const displayLoans = (loans ?? []).map(loan => {
        const amount = loan.transactions?.amount || 0;
        const driverName = driverProfileName(loan.contracts?.drivers?.profiles) ?? 'N/A';
        return {
            ...loan,
            driverName,
            amount,
            total: amount * (1 + (loan.interest_rate || 0) / 100),
            transactionDate: loan.transactions?.transaction_at,
        };
    });

    return (
        <Card className="border-0">
            <CardHeader>
                <div className="flex flex-wrap items-center gap-4">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search advances..."
                            className="pl-9"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                    <ListPagination
                        currentPage={paginationWithCount.currentPage}
                        totalPages={paginationWithCount.totalPages}
                        onPageChange={paginationWithCount.goToPage}
                        totalItems={paginationWithCount.totalItems}
                    />
                {isLoading ? (
                    <Skeleton className="h-48 w-full" />
                ) : loans && loans.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <SortableTableHead sortKey="created_at" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort}>
                                    Date
                                </SortableTableHead>
                                <SortableTableHead>Driver</SortableTableHead>
                                <SortableTableHead>Principal</SortableTableHead>
                                <SortableTableHead sortKey="interest_rate" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort}>
                                    Interest
                                </SortableTableHead>
                                <SortableTableHead align="right">Total</SortableTableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {displayLoans.map((loan) => (
                                <TableRow key={loan.id}>
                                    <TableCell>{formatBusinessDate(loan.transactionDate)}</TableCell>
                                    <TableCell>{loan.driverName}</TableCell>
                                    <TableCell>{formatCurrency(loan.amount)}</TableCell>
                                    <TableCell>{loan.interest_rate}%</TableCell>
                                    <TableCell className="text-right font-medium">{formatCurrency(loan.total)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <p className="text-center text-muted-foreground py-8">No advances found</p>
                )}
            </CardContent>
        </Card>
    );
}
